import { useAnalyticsTracking, useEmitUiEvent } from '@montugroup/data-collection';
import type { WaitlistAvailability } from '@montugroup/pms-api-contracts';
import { useCallback, useLayoutEffect, useRef } from 'react';

type WaitlistEventName = 'waitlist_joined' | 'waitlist_preference_changed' | 'waitlist_left';

type PostBookingEventName =
  | 'waitlist_post_booking_prompt_shown'
  | 'waitlist_post_booking_prompt_dismissed'
  | 'waitlist_post_booking_lookup_timed_out';

export type WaitlistJoinSource = 'card' | 'post_booking_modal';

export type PostBookingTrigger = 'booking' | 'reschedule';

// the ui_event schema is strict, so the join source is carried by the component name
const COMPONENT_FOR_SOURCE: Record<WaitlistJoinSource, string> = {
  card: 'ConsultationWaitlistSection',
  post_booking_modal: 'PostBookingWaitlistDialog',
};

export const useConsultationWaitlistTracking = () => {
  // useEmitUiEvent returns a new function on every render; reading it through a ref keeps these callbacks stable
  const emitEvent = useEmitUiEvent();
  const emitEventRef = useRef(emitEvent);
  // updated in a layout effect, as a render-time ref write makes the React Compiler skip the hook
  useLayoutEffect(() => {
    emitEventRef.current = emitEvent;
  }, [emitEvent]);
  const { uiEvent } = useAnalyticsTracking();
  const contextId = uiEvent?.context_id;

  // The waitlist section isn't wrapped by the analytics consultation context, so the consultation is
  // attached manually, as useClinicTelecomTracking does.
  const trackWaitlistEvent = useCallback(
    (
      eventName: WaitlistEventName,
      consultationId: number,
      availability: WaitlistAvailability | null,
      source: WaitlistJoinSource = 'card',
    ) => {
      emitEventRef.current(
        {
          component_name: COMPONENT_FOR_SOURCE[source],
          event_name: eventName,
          id: `consultation_waitlist_${consultationId}`,
          value: availability,
          context_id: contextId,
        },
        { consultation: { consultation_id: String(consultationId) } },
      );
    },
    [contextId],
  );

  const trackWaitlistJoined = useCallback(
    (consultationId: number, availability: WaitlistAvailability, source: WaitlistJoinSource = 'card') =>
      trackWaitlistEvent('waitlist_joined', consultationId, availability, source),
    [trackWaitlistEvent],
  );

  const trackWaitlistPreferenceChanged = useCallback(
    (consultationId: number, availability: WaitlistAvailability) =>
      trackWaitlistEvent('waitlist_preference_changed', consultationId, availability),
    [trackWaitlistEvent],
  );

  const trackWaitlistLeft = useCallback(
    (consultationId: number, availability: WaitlistAvailability | null) =>
      trackWaitlistEvent('waitlist_left', consultationId, availability),
    [trackWaitlistEvent],
  );

  // the trigger is carried in value; the lookup can time out before a consultation is known
  const trackPostBookingEvent = useCallback(
    (eventName: PostBookingEventName, trigger: PostBookingTrigger, consultationId: number | null) => {
      emitEventRef.current(
        {
          component_name: COMPONENT_FOR_SOURCE.post_booking_modal,
          event_name: eventName,
          id: consultationId ? `post_booking_waitlist_${consultationId}` : 'post_booking_waitlist',
          value: trigger,
          context_id: contextId,
        },
        consultationId ? { consultation: { consultation_id: String(consultationId) } } : {},
      );
    },
    [contextId],
  );

  const trackPostBookingPromptShown = useCallback(
    (trigger: PostBookingTrigger, consultationId: number) =>
      trackPostBookingEvent('waitlist_post_booking_prompt_shown', trigger, consultationId),
    [trackPostBookingEvent],
  );

  const trackPostBookingPromptDismissed = useCallback(
    (trigger: PostBookingTrigger, consultationId: number) =>
      trackPostBookingEvent('waitlist_post_booking_prompt_dismissed', trigger, consultationId),
    [trackPostBookingEvent],
  );

  const trackPostBookingLookupTimedOut = useCallback(
    (trigger: PostBookingTrigger) => trackPostBookingEvent('waitlist_post_booking_lookup_timed_out', trigger, null),
    [trackPostBookingEvent],
  );

  return {
    trackWaitlistJoined,
    trackWaitlistPreferenceChanged,
    trackWaitlistLeft,
    trackPostBookingPromptShown,
    trackPostBookingPromptDismissed,
    trackPostBookingLookupTimedOut,
  };
};
